import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { BoutRow } from '../components/BoutRow'
import { SECTION_LABELS, type Fixture } from '../lib/fighters'

type PmBout = {
  fixtureId: string
  redName: string
  blueName: string
  eventSlug: string | null
  resolved: boolean
  pending: boolean
  missing: boolean
  winnerName: string | null
  method: string | null
  round: string | null
  confidence: string
  notes: string[]
}

type PmStatus = {
  complete: boolean
  resolvedCount: number
  totalCount: number
  alreadySettled?: boolean
  fetchedAt?: string
  bouts: PmBout[]
  errors?: string[]
}

export function EventPage() {
  const { id } = useParams()
  const { token, refresh, user } = useAuth()
  const [data, setData] = useState<any>(null)
  const [standings, setStandings] = useState<any>(null)
  const [pm, setPm] = useState<PmStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const res = await api(`/api/competitions/${id}`, { token })
    setData(res)
    const poolId = (res as any).pools?.[0]?.id
    if (poolId) {
      const s = await api(`/api/pools/${poolId}/standings`, { token })
      setStandings(s)
    }
  }, [id, token])

  const loadPolymarket = useCallback(async () => {
    try {
      const status = await api<PmStatus>(`/api/competitions/${id}/results/polymarket`, { token })
      setPm(status)
      return status
    } catch (e: any) {
      // non-fatal — event still usable offline
      setPm(null)
      return null
    }
  }, [id, token])

  useEffect(() => {
    load().catch((e) => setError(e.message))
    loadPolymarket()
  }, [load, loadPolymarket])

  // Poll Polymarket and auto-settle when the card is fully resolved
  useEffect(() => {
    if (!id || !token) return
    let cancelled = false
    const tick = async () => {
      if (cancelled) return
      try {
        const status = await loadPolymarket()
        if (!status || status.alreadySettled || !status.complete) return
        // Card fully resolved on Polymarket — apply + settle without host typing
        const res = await api(`/api/competitions/${id}/results/polymarket`, {
          method: 'POST',
          token,
          idempotencyKey: `polymarket-auto-${id}`,
          body: {},
        })
        if (cancelled) return
        setMsg(
          `Results pulled from Polymarket · ${(res as any).settlements?.map((s: any) => s.gate?.status || s.receipt?.status).join(', ') || 'settled'}`
        )
        await refresh()
        await load()
        await loadPolymarket()
      } catch {
        /* wait for next poll */
      }
    }
    const interval = setInterval(tick, 60_000)
    // light delayed first auto-attempt (status already loaded above)
    const first = setTimeout(tick, 12_000)
    return () => {
      cancelled = true
      clearInterval(interval)
      clearTimeout(first)
    }
  }, [id, token, load, loadPolymarket, refresh])

  const fixtures: Fixture[] = data?.competition?.fixtures || []
  const bySection = useMemo(() => {
    const map = new Map<string, Fixture[]>()
    for (const f of fixtures) {
      const sec = f.section || 'main'
      if (!map.has(sec)) map.set(sec, [])
      map.get(sec)!.push(f)
    }
    return map
  }, [fixtures])

  const pmByFixture = useMemo(() => {
    const m = new Map<string, PmBout>()
    for (const b of pm?.bouts || []) m.set(b.fixtureId, b)
    return m
  }, [pm])

  if (!data) return <div className="panel">{error || 'Loading event…'}</div>
  const { competition, pools, rooms } = data
  const pool = pools?.[0]
  const room = rooms?.[0]
  const assets = competition.assets || {}
  const isHost = user?.id && competition.hostUserId === user.id
  const settled =
    pm?.alreadySettled ||
    pool?.status === 'settled' ||
    pool?.status === 'disputed' ||
    Boolean(standings?.rows?.length)

  async function joinPool() {
    setError(null)
    setBusy(true)
    try {
      await api(`/api/pools/${pool.id}/join`, {
        method: 'POST',
        token,
        idempotencyKey: `join-${pool.id}`,
      })
      setMsg('Joined pool — $25 demo credits held.')
      await refresh()
      await load()
    } catch (e: any) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  async function pullPolymarket(force = false) {
    setError(null)
    setBusy(true)
    setMsg(null)
    try {
      const res = await api(`/api/competitions/${competition.id}/results/polymarket`, {
        method: 'POST',
        token,
        idempotencyKey: `polymarket-pull-${competition.id}-${force ? 'force' : 'full'}`,
        body: { force },
      })
      setMsg(
        `Polymarket results applied · ${(res as any).settlements?.map((s: any) => s.gate?.status || s.receipt?.status).join(', ') || 'ok'}`
      )
      await refresh()
      await load()
      await loadPolymarket()
    } catch (e: any) {
      const body = e.body as any
      if (body?.error === 'polymarket_incomplete') {
        setPm(body)
        setMsg(
          `Waiting on Polymarket — ${body.resolvedCount}/${body.totalCount} bouts resolved. Settlement runs automatically when the card is complete.`
        )
      } else {
        setError(e.message || 'Polymarket pull failed')
      }
      await loadPolymarket()
    } finally {
      setBusy(false)
    }
  }

  async function startTrivia() {
    const game = await api<{ game: { id: string } }>('/api/games', {
      method: 'POST',
      token,
      body: { gameType: 'trivia-duel', roomId: room?.id },
    })
    window.location.href = `/games/${game.game.id}`
  }

  return (
    <div className="stack">
      <section className="hero-fight">
        <div
          className="bg"
          style={{ backgroundImage: `url(${assets.hero || '/assets/mma/hero-backdrop/wide.png'})` }}
        />
        <div className="veil" />
        <div className="content">
          <div className="eyebrow">{competition.template}</div>
          <h1>{competition.title}</h1>
          <div className="meta">
            <span>{competition.status}</span>
            <span>{fixtures.length} bouts</span>
            <span>v{competition.version}</span>
            {pool && <span>${pool.stakeUnits} stake</span>}
            {pm && (
              <span>
                Polymarket {pm.resolvedCount}/{pm.totalCount}
                {pm.complete ? ' · complete' : ''}
              </span>
            )}
          </div>
          {error && <div className="error">{error}</div>}
          {msg && <div className="success">{msg}</div>}
          <div className="hero-actions">
            {room && (
              <Link className="btn secondary" to={`/rooms/${room.id}`}>
                Watch room
              </Link>
            )}
            <button className="btn" onClick={joinPool} disabled={busy} data-testid="join-pool">
              Join ${pool?.stakeUnits || 25} pool
            </button>
            <Link className="btn secondary" to={`/pools/${pool?.id}/picks`}>
              Pick workbench
            </Link>
            <button
              className="btn"
              onClick={() => pullPolymarket(false)}
              disabled={busy || settled}
              data-testid="pull-polymarket"
              title="Pull oracle-resolved results from Polymarket and settle"
            >
              {settled ? 'Results locked' : busy ? 'Syncing…' : 'Sync results (Polymarket)'}
            </button>
            {isHost && !settled && pm && pm.resolvedCount > 0 && !pm.complete && (
              <button
                className="btn secondary"
                onClick={() => pullPolymarket(true)}
                disabled={busy}
                title="Settle with only the bouts Polymarket has resolved so far"
              >
                Settle partial card
              </button>
            )}
            <button className="btn ghost" onClick={startTrivia}>
              Start trivia
            </button>
          </div>
          <p className="muted" style={{ color: '#cbd5e1', margin: '10px 0 0', maxWidth: 560 }}>
            Fight results are pulled automatically from{' '}
            <strong>Polymarket</strong> when markets resolve — no manual host entry. This page and
            the server poll in the background.
          </p>
        </div>
      </section>

      <div className="grid two">
        <section className="panel">
          <h2>Fight card</h2>
          {['main', 'prelim', 'early'].map((sec) => {
            const list = bySection.get(sec)
            if (!list?.length) return null
            return (
              <div key={sec}>
                <div className="section-label">{SECTION_LABELS[sec] || sec}</div>
                {list.map((f) => {
                  const st = pmByFixture.get(f.fixtureId)
                  return (
                    <div key={f.fixtureId}>
                      <BoutRow fixture={f} />
                      {st && (
                        <div className={`pm-bout-status ${st.resolved ? 'ok' : st.missing ? 'miss' : 'pend'}`}>
                          {st.resolved ? (
                            <>
                              <strong>Polymarket:</strong> {st.winnerName}
                              {st.method ? ` · ${st.method}` : ''}
                              {st.round ? ` · ${st.round}` : ''}
                            </>
                          ) : st.missing ? (
                            <>No Polymarket market matched</>
                          ) : (
                            <>Waiting on Polymarket resolution…</>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })}
          {[...bySection.keys()]
            .filter((s) => !['main', 'prelim', 'early'].includes(s))
            .map((sec) => (
              <div key={sec}>
                <div className="section-label">{SECTION_LABELS[sec] || sec}</div>
                {bySection.get(sec)!.map((f) => (
                  <BoutRow key={f.fixtureId} fixture={f} />
                ))}
              </div>
            ))}
        </section>

        <div className="stack">
          <section className="panel">
            <h2>Pool · ${pool?.stakeUnits || 25} demo</h2>
            <p className="muted">
              {pool?.name} · {pool?.status} · player-prop scoring (winner + method + round)
            </p>
            <div
              style={{
                marginTop: 10,
                height: 72,
                borderRadius: 4,
                backgroundImage: `url(${assets.accent || '/assets/mma/pool-card-accent/horizontal-strip.png'})`,
                backgroundSize: 'cover',
                border: '1px solid var(--bevel-lo)',
              }}
            />
          </section>

          <section className="panel">
            <h2>Polymarket feed</h2>
            {!pm ? (
              <p className="muted">Checking Gamma API…</p>
            ) : (
              <>
                <p className="muted" style={{ marginTop: 0 }}>
                  {pm.resolvedCount}/{pm.totalCount} bouts resolved
                  {pm.fetchedAt ? ` · updated ${new Date(pm.fetchedAt).toLocaleTimeString()}` : ''}
                  {pm.alreadySettled ? ' · pool settled' : pm.complete ? ' · ready to settle' : ' · watching'}
                </p>
                <ul className="pm-list">
                  {(pm.bouts || []).map((b) => (
                    <li key={b.fixtureId}>
                      <span className={`dot ${b.resolved ? 'ok' : b.missing ? 'miss' : 'pend'}`} />
                      <div>
                        <strong>
                          {b.redName} vs {b.blueName}
                        </strong>
                        <div className="sub muted">
                          {b.resolved
                            ? `${b.winnerName} · ${b.method || '—'} · ${b.round || '—'}`
                            : b.missing
                              ? 'not listed'
                              : 'live market — not resolved'}
                          {b.eventSlug ? ` · ${b.eventSlug}` : ''}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          <section className="panel">
            <h2>Standings</h2>
            {!standings?.rows?.length ? (
              <p className="muted">Standings appear after Polymarket results settle the pool.</p>
            ) : (
              <table className="table" data-testid="standings">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>User</th>
                    <th>Score</th>
                    <th>Correct</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.rows.map((r: any) => (
                    <tr key={r.userId} className={r.rank === 1 ? 'rank-1' : ''}>
                      <td>{r.rank}</td>
                      <td>{String(r.userId).slice(0, 8)}…</td>
                      <td>{r.score}</td>
                      <td>
                        {r.correctCount}/{r.scoredCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>
      </div>
    </div>
  )
}
